import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth, useEtiquetaCritico, useNorma } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, useCan } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, XCircle, ShieldAlert, FileText, Wrench } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IAExplain } from "@/components/IAExplain";
import { COBERTURA_STYLE, estadoLabel, resumenCobertura, ESTADO } from "@/lib/cobertura";
import { verifyAttrs } from "@/verify/contract";
import { toast } from "sonner";

const FILTROS = [
  { k: "todos", label: "Todos" },
  { k: "cubierto", label: "Cubiertos" },
  { k: "con_reservas", label: "Con reservas" },
  { k: "sin_evidencia", label: "Sin evidencia" },
];

function EstadoBadge({ r }) {
  const st = COBERTURA_STYLE[r.estado_cobertura] || COBERTURA_STYLE.sin_evidencia;
  const Icon = r.estado_cobertura === ESTADO.CUBIERTO ? CheckCircle2
    : r.estado_cobertura === ESTADO.CON_RESERVAS ? AlertTriangle : XCircle;
  const motivos = (r.motivo_reservas || []).slice(0, 3).join(" · ");
  const title = r.pendiente_confirmacion ? "Requisito KO pendiente de tu confirmación" : motivos;
  return (
    <span data-testid={`estado-badge-${r.codigo}`} title={title || undefined}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border"
      style={{ color: st.color, backgroundColor: st.bg, borderColor: st.border }}>
      <Icon size={12} /> {estadoLabel(r)}
    </span>
  );
}

export default function Norma() {
  const { user, scopeTenant } = useAuth();
  const krit = useEtiquetaCritico();
  const norma = useNorma();
  const canEdit = useCan(user);
  const navigate = useNavigate();
  const needsScope = useScopeGuard(user, scopeTenant);
  const [cob, setCob] = useState(null);
  const [filtro, setFiltro] = useState("todos");
  const [koQueue, setKoQueue] = useState([]);
  const [koIdx, setKoIdx] = useState(0);
  const [koOpen, setKoOpen] = useState(false);
  const [revisado, setRevisado] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (needsScope) return;
    const { data } = await api.get("/norma/cobertura");
    setCob(data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const resumen = useMemo(() => resumenCobertura(cob), [cob]);
  const koPendientes = useMemo(() => {
    if (!cob) return [];
    const out = [];
    cob.capitulos.forEach((c) => c.requisitos.forEach((r) => {
      if (r.es_critico && r.pendiente_confirmacion) out.push(r);
    }));
    return out;
  }, [cob]);

  const matchFiltro = (r) => filtro === "todos" || r.estado_cobertura === filtro;

  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("revisar") === "ko" && koPendientes.length > 0) {
      setKoQueue(koPendientes); setKoIdx(0); setRevisado(false); setKoOpen(true);
    }
  }, [searchParams, koPendientes]);

  const abrirRevisionKO = () => {
    if (koPendientes.length === 0) return;
    setKoQueue(koPendientes); setKoIdx(0); setRevisado(false); setKoOpen(true);
  };
  const abrirConfirmarUno = (r) => {
    setKoQueue([r]); setKoIdx(0); setRevisado(false); setKoOpen(true);
  };

  const actual = koQueue[koIdx];

  const confirmar = async () => {
    if (!actual) return;
    const docId = actual.documentos?.[0]?.id;
    if (!docId) { toast.error("El requisito no tiene documento asociado"); return; }
    setSaving(true);
    try {
      await api.post("/documentos/confirmar-ko", { documento_id: docId, numero: actual.codigo });
      toast.success(`KO ${actual.codigo} confirmado`);
      if (koIdx + 1 < koQueue.length) { setKoIdx(koIdx + 1); setRevisado(false); }
      else { setKoOpen(false); }
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const retirar = async (r) => {
    const docId = r.documentos?.[0]?.id;
    if (!docId) return;
    try {
      await api.delete("/documentos/confirmar-ko", { data: { documento_id: docId, numero: r.codigo } });
      toast.success(`Confirmación de ${r.codigo} retirada`);
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;
  if (!cob) return <div className="text-[#0E2A47]/60">Cargando mapa normativo…</div>;

  return (
    <div data-testid="norma-page">
      <PageHeader overline="Nivel 1 · Documenta" title={`Mapa Normativo ${norma.nombre}`} />

      <Card className="p-6 border-[#E2E8F0] mb-4" data-testid="cobertura-global-card"
        {...verifyAttrs("CoberturaRequisitos", resumen)}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold uppercase tracking-wider text-[#0E2A47]/60">Cobertura verificada</span>
          <span className="text-2xl font-black tracking-tighter text-[#0E2A47]" data-testid="cobertura-pct">{cob.cobertura_pct}%</span>
        </div>
        <Progress value={cob.cobertura_pct} className="h-3" />
        <div className="text-xs text-[#0E2A47]/60 mt-2" data-testid="cobertura-desglose">
          {cob.cubiertos} de {cob.total} cubiertos ·{" "}
          <span className="text-[#B45309] font-semibold">{cob.con_reservas} con reservas</span> ·{" "}
          <span className="text-[#EF4444] font-semibold">{cob.sin_evidencia} sin evidencia</span>
          <span className="text-[#0E2A47]/40"> · las reservas no suman a la cobertura</span>
        </div>
        {koPendientes.length > 0 && canEdit && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#F59E0B]/40 bg-[#FFFBEB] p-3">
            <ShieldAlert size={16} className="text-[#B45309]" />
            <span className="text-sm text-[#0E2A47]/80 flex-1">
              Tienes <b>{koPendientes.length}</b> {krit} pendiente{koPendientes.length === 1 ? "" : "s"} de tu confirmación.
            </span>
            <Button size="sm" onClick={abrirRevisionKO} data-testid="revisar-ko-btn" className="bg-[#FF5625] hover:bg-[#E0451A]">
              Revisar {krit} pendientes ({koPendientes.length})
            </Button>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="cobertura-filtros">
        {FILTROS.map((f) => (
          <button key={f.k} onClick={() => setFiltro(f.k)} data-testid={`filtro-${f.k}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${filtro === f.k ? "bg-[#0E2A47] text-white border-[#0E2A47]" : "bg-white text-[#0E2A47]/70 border-[#E2E8F0] hover:border-[#0E2A47]/40"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <Accordion type="multiple" className="space-y-3">
        {cob.capitulos.map((cap) => {
          const reqs = cap.requisitos.filter(matchFiltro);
          if (reqs.length === 0) return null;
          return (
            <AccordionItem key={cap.capitulo} value={cap.capitulo} className="border border-[#E2E8F0] rounded-lg bg-white px-4"
              data-testid={`capitulo-${cap.capitulo}`}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <span className="font-semibold text-[#0E2A47] text-left">
                    <span className="font-mono text-[#FF5625] mr-2">Cap. {cap.capitulo}</span>{cap.titulo}
                  </span>
                  <span className="text-xs text-[#0E2A47]/50">{cap.cubiertos}/{cap.total}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-1 pb-2">
                  {reqs.map((r) => (
                    <div key={r.codigo} className="py-2 border-t border-[#F1F5F9]" data-testid={`req-${r.codigo}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs text-[#FF5625]">{r.codigo}</span>
                            {r.ko && <Badge className="bg-[#0E2A47] hover:bg-[#0E2A47] text-[10px] px-1.5 py-0">{krit}</Badge>}
                            <EstadoBadge r={r} />
                          </div>
                          <div className="text-sm text-[#0E2A47]/80 mt-1">{r.texto}</div>
                          <div className="mt-1"><IAExplain numero={r.codigo} texto={r.texto} /></div>

                          {r.estado_cobertura === ESTADO.CON_RESERVAS && (() => {
                            const MOTIVO_CONF = "El mapeo automático está por debajo del umbral de confianza (70%).";
                            const requiereCorreccion = (r.motivo_reservas || []).some((m) => m && m !== MOTIVO_CONF);
                            if (!(r.motivo_reservas?.length > 0) && !r.pendiente_confirmacion) return null;
                            const border = requiereCorreccion ? "border-[#EF4444]/40" : "border-[#F59E0B]/40";
                            const bg = requiereCorreccion ? "bg-[#FEF2F2]" : "bg-[#FFFBEB]";
                            const color = requiereCorreccion ? "text-[#DC2626]" : "text-[#B45309]";
                            const label = requiereCorreccion ? "Requiere corrección · corrige el documento" : "Necesita tu revisión · confírmalo";
                            const motivos = r.motivo_reservas?.length > 0 ? r.motivo_reservas : ["Requisito KO pendiente de tu confirmación."];
                            return (
                              <div className={`mt-2 rounded-lg border ${border} ${bg} p-3`} data-testid={`reservas-${r.codigo}`}>
                                <div className={`text-[11px] font-bold uppercase tracking-wider ${color} mb-1`}>{label}</div>
                                <ul className="list-disc pl-4 space-y-0.5">
                                  {motivos.map((m, i) => (
                                    <li key={i} className="text-xs text-[#0E2A47]/75">{m}</li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })()}

                          {r.confirmacion_humana?.confirmado && (
                            <div className="text-[11px] text-[#15803D] mt-1" data-testid={`confirmado-${r.codigo}`}>
                              Confirmado por {r.confirmacion_humana.usuario || "—"}{r.confirmacion_humana.fecha ? ` el ${String(r.confirmacion_humana.fecha).slice(0, 10)}` : ""}
                              {canEdit && <button onClick={() => retirar(r)} data-testid={`retirar-ko-${r.codigo}`} className="ml-2 underline text-[#B45309] hover:text-[#EF4444]">Retirar confirmación</button>}
                            </div>
                          )}

                          {r.documentos?.length > 0 && (
                            <div className="text-xs text-[#0E2A47]/50 mt-1 flex flex-col gap-1">
                              {r.documentos.map((d) => (
                                <span key={d.id} className="inline-flex items-center gap-1 flex-wrap">
                                  <FileText size={11} /> {d.titulo}{typeof d.confianza === "number" ? ` (${d.confianza}%)` : ""}
                                  {d.avisos_ia?.length > 0 && <span className="text-[#B45309]"> · {d.avisos_ia.length} aviso(s) IA</span>}
                                  {d.avisos_ia?.length > 0 && canEdit && (
                                    <button onClick={() => navigate(`/documentos?doc=${d.id}`)} data-testid={`corregir-doc-${r.codigo}-${d.id}`}
                                      className="inline-flex items-center gap-1 text-[#FF5625] hover:underline font-semibold">
                                      <Wrench size={11} /> Corregir en el documento
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {r.es_critico && r.pendiente_confirmacion && canEdit && (
                          <Button size="sm" onClick={() => abrirConfirmarUno(r)} data-testid={`confirmar-cobertura-${r.codigo}`}
                            className="bg-[#FF5625] hover:bg-[#E0451A] shrink-0">Confirmar cobertura</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <Dialog open={koOpen} onOpenChange={setKoOpen}>
        <DialogContent className="bg-white" data-testid="ko-confirm-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Vas a confirmar un requisito {krit}</DialogTitle></DialogHeader>
          {actual && (
            <div className="space-y-3">
              {koQueue.length > 1 && (
                <div className="text-xs text-[#0E2A47]/50" data-testid="ko-progress">Revisando {koIdx + 1} de {koQueue.length}</div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-[#FF5625]">{actual.codigo}</span>
                  <Badge className="bg-[#0E2A47] hover:bg-[#0E2A47] text-[10px]">{krit}</Badge>
                </div>
                <div className="text-sm text-[#0E2A47]/80 mt-1">{actual.texto}</div>
              </div>
              <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#0E2A47]/50 mb-1">Documento(s) que lo respaldan</div>
                {(actual.documentos || []).map((d) => (
                  <div key={d.id} className="text-sm text-[#0E2A47]/80 flex items-center gap-1"><FileText size={12} /> {d.titulo}</div>
                ))}
                {(actual.documentos || []).length === 0 && <div className="text-sm text-[#EF4444]">Sin documento asociado.</div>}
              </div>
              <p className="text-sm text-[#0E2A47]/70">
                Confirmas que has revisado este documento y que cubre el requisito. AuditReady registra tu confirmación; la responsabilidad de la evidencia es tuya.
              </p>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={revisado} onCheckedChange={(v) => setRevisado(!!v)} data-testid="ko-revisado" />
                He revisado la evidencia
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setKoOpen(false)} data-testid="ko-cancelar">Cancelar</Button>
            <Button onClick={confirmar} disabled={!revisado || saving} data-testid="ko-confirmar"
              className="bg-[#FF5625] hover:bg-[#E0451A]">{saving ? "Confirmando…" : "Confirmar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
